import React from 'react';
import { Heart, Repeat } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";

interface SimulatedTweetProps {
  text: string;
  url?: string;
  date?: string;
  authorName?: string;
  authorHandle?: string;
  authorAvatar?: string;
  mediaUrl?: string;
  likeCount?: number;
  retweetCount?: number;
}

const SimulatedTweet: React.FC<SimulatedTweetProps> = ({ 
  text, 
  url, 
  date, 
  authorName, 
  authorHandle, 
  authorAvatar,
  mediaUrl,
  likeCount,
  retweetCount
}) => {
  const handle = authorHandle || url?.split('x.com/')[1]?.split('/')[0] || 'creator';
  const name = authorName || 'Developer';
  
  const formattedDate = date ? new Date(date).toLocaleDateString(undefined, { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }) : null;

  const cleanText = text
    .replace(/https:\/\/t\.co\/\w+$/g, '')
    .split('[QUOTED TWEET]:')[0]
    .split('[QUOTED TEXT]:')[0]
    .trim();
  
  return (
    <div className="p-4 w-[320px] space-y-4">
      <div className="flex gap-3">
        <Avatar className="h-10 w-10 border border-border">
          <AvatarImage src={authorAvatar} />
          <AvatarFallback className="bg-card">
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-foreground">
              <path d="M18.244 2.25h3.308l-7.227 3.779 8.502 11.221h-6.657l-5.214-6.817-5.966 6.817H1.676l7.728-8.831L1.242 2.25h6.826l4.704 6.231 5.472-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
            </svg>
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center justify-between w-full">
            <span className="text-sm font-black text-foreground leading-none truncate">{name}</span>
            {formattedDate && <span className="text-[10px] text-muted-foreground whitespace-nowrap ml-2">{formattedDate}</span>}
          </div>
          <span className="text-xs text-muted-foreground font-medium">{handle.startsWith('@') ? handle : `@${handle}`}</span>
        </div>
      </div>
      
      <p className="text-xs text-foreground leading-relaxed font-medium">
        {cleanText}
      </p>

      {mediaUrl && (
        <div className="rounded-xl overflow-hidden border border-border shadow-2xl">
          <img src={mediaUrl} alt="" className="w-full h-auto max-h-[180px] object-cover" />
        </div>
      )}
      
      <div className="flex items-center gap-4 text-muted-foreground">
        <div className="flex items-center gap-1.5 text-[10px] font-bold">
          <Heart className="h-3 w-3" /> {likeCount || 0}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-bold">
          <Repeat className="h-3 w-3" /> {retweetCount || 0}
        </div>
        {url && (
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="ml-auto text-primary hover:underline text-[10px] font-black uppercase tracking-widest"
          >
            View Post
          </a>
        )}
      </div>
    </div>
  );
};

export default SimulatedTweet;
